import { DatePicker } from '@mui/x-date-pickers';
import { Field, FieldProps, useFormikContext } from 'formik';
import { camelCase, get } from 'lodash';
import moment, { Moment } from 'moment';
import React, { useCallback, useMemo, useState } from 'react';

import { InputComponent } from '..';
import { normalizeSize } from '../helpers';
import {
  MAX_PLAUSIBLE_YEARS_AHEAD,
  MIN_PLAUSIBLE_YEAR,
  plausibleMaxDate,
  plausibleMinDate,
} from '../../date-bounds';

const DATE_FORMAT = 'YYYY-MM-DD';

export interface InputDateProps extends Omit<InputComponent, 'size'> {
  min?: string | Moment;
  max?: string | Moment;
  size?: InputComponent['size'] | 'small' | 'medium' | 'large';
  onChange?: (name: string, value: string) => void;
  openOnFocus?: boolean;
}

export function InputDate({
  label,
  disabled,
  hidden,
  required,
  className,
  hint,
  nameOverride,
  placeholder,
  showLabel = !hidden,
  readOnly = false,
  min = undefined,
  max = undefined,
  size: propSize = 'medium',
  onChange = undefined,
  openOnFocus = true,
}: InputDateProps) {
  const { errors, handleChange, setFieldTouched } =
    useFormikContext<Record<string, string>>();

  const [isOpen, setIsOpen] = useState(false);

  const validate = useCallback(
    (value: string | Moment | undefined) => {
      if (required && (!value || value === undefined)) {
        return `${label} is required.`;
      }

      if (value) {
        const momentValue = moment.isMoment(value) ? value : moment(value);
        if (!momentValue.isValid()) {
          return `${label} must be a valid date.`;
        }

        // A two-digit year parses fine (0026 is a real date), so the plain
        // validity check above lets it through — bound it explicitly.
        const year = momentValue.year();
        if (year < MIN_PLAUSIBLE_YEAR) {
          return `${label} must have a four-digit year.`;
        }
        if (year > moment().year() + MAX_PLAUSIBLE_YEARS_AHEAD) {
          return `${label} is too far in the future.`;
        }

        if (min) {
          const minMoment = moment.isMoment(min) ? min : moment(min);
          if (momentValue.isBefore(minMoment)) {
            return `${label} must be after ${minMoment.format('MM/DD/YYYY')}.`;
          }
        }

        if (max) {
          const maxMoment = moment.isMoment(max) ? max : moment(max);
          if (momentValue.isAfter(maxMoment)) {
            return `${label} must be before ${maxMoment.format('MM/DD/YYYY')}.`;
          }
        }
      }

      return undefined;
    },
    [label, required, min, max],
  );

  const name = useMemo(
    () => nameOverride ?? camelCase(label),
    [label, nameOverride],
  );

  const error = useMemo(() => get(errors, name), [errors, name]);

  const inputPlaceholder = useMemo(
    () => placeholder ?? label?.replace(/[?:]/g, ''),
    [placeholder, label],
  );

  const size = normalizeSize(propSize);

  // Default bounds keep the calendar (and MUI X's own field validation) inside
  // plausible dates when a caller passes no explicit range.
  const minDate = useMemo(() => {
    if (!min) return plausibleMinDate();
    return moment.isMoment(min) ? min : moment(min);
  }, [min]);

  const maxDate = useMemo(() => {
    if (!max) return plausibleMaxDate();
    return moment.isMoment(max) ? max : moment(max);
  }, [max]);

  const handleDateChange = useCallback(
    (date: Moment | null) => {
      const dateValue = date ? date.format(DATE_FORMAT) : '';
      onChange?.(name, dateValue);
      // For Formik integration, we need to simulate an event
      const syntheticEvent = {
        target: { name, value: dateValue },
        currentTarget: { name, value: dateValue },
      } as React.ChangeEvent<HTMLInputElement>;
      handleChange(syntheticEvent);
      // Formik's `handleChange` does not set `touched`, and the picker never
      // fires a native blur on the underlying input, so without this a date the
      // user set stays untouched. Forms that branch on `touched` (e.g. the head
      // form's Cut Date auto-fill guard) would keep overwriting their entry.
      setFieldTouched(name, true, false);
      setIsOpen(false);
    },
    [name, onChange, handleChange, setFieldTouched],
  );

  const handleBlur = useCallback(() => {
    setFieldTouched(name, true);
  }, [name, setFieldTouched]);

  const handleFocus = useCallback(() => {
    if (openOnFocus && !readOnly && !disabled) {
      setIsOpen(true);
    }
  }, [openOnFocus, readOnly, disabled]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  return (
    <Field name={name} validate={validate}>
      {({ field }: FieldProps) => (
        <DatePicker
          label={showLabel ? label : undefined}
          value={field.value ? moment(field.value) : null}
          onChange={handleDateChange}
          disabled={disabled}
          readOnly={readOnly}
          minDate={minDate}
          maxDate={maxDate}
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          slotProps={{
            textField: {
              name,
              fullWidth: true,
              variant: 'outlined' as const,
              className,
              hidden,
              required: required && !readOnly,
              placeholder: inputPlaceholder,
              size: size === 'large' ? 'medium' : size,
              error: !!error,
              helperText: error ? error : hint,
              onBlur: handleBlur,
              ...(openOnFocus && { onClick: handleFocus }),
            },
          }}
        />
      )}
    </Field>
  );
}
